import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip as MuiTooltip,
    Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import moment from 'moment';
import Layout from './Layout';
import { CumulativePaceChart, DailyDispatchChart } from './MonthlyDispatchTrackerCharts';
import { getMonthlyDispatchTracker } from '../services/api';
import { useSnackbar } from '../contexts/SnackbarContext';
import { formatDay, formatNumber, formatPct, pctColor } from '../utils/monthlyDispatchTrackerFormat';

const DEFAULT_BASELINE_MONTH = '2026-09';
const OVERTIME_COLOR = '#ed6c02';
const METRIC_DISPATCHED = 'dispatched';
const METRIC_DUE = 'due';

const getMonthSubtitle = (month) => {
    if (month.isComplete) return '(Full month)';
    return '(Month to date)';
};

const csvEscape = (value) => {
    const text = value === null || value === undefined ? '' : String(value);
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

function SummaryCard({ title, value, detail, accent = 'primary.main', valueColor = 'text.primary' }) {
    return (
        <Paper elevation={1} sx={{ p: 2, borderLeft: 4, borderColor: accent, borderRadius: 1, flex: '1 1 220px' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                {title}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: valueColor, fontVariantNumeric: 'tabular-nums' }}>
                {value}
            </Typography>
            {detail && (
                <Typography variant="caption" color="text.secondary">
                    {detail}
                </Typography>
            )}
        </Paper>
    );
}

const MonthlyDispatchTracker = () => {
    const { showSnackbar } = useSnackbar();
    const [baselineMonth, setBaselineMonth] = useState(DEFAULT_BASELINE_MONTH);
    const [asOfDate, setAsOfDate] = useState('');
    const [metric, setMetric] = useState(METRIC_DISPATCHED);
    const [projectOvertime, setProjectOvertime] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const payload = await getMonthlyDispatchTracker({ baselineMonth, asOfDate });
            setData(payload);
        } catch (error) {
            const message = error?.response?.data?.message || error?.message || 'Failed to load the monthly dispatch tracker.';
            showSnackbar(message, 'error');
        } finally {
            setLoading(false);
        }
    }, [baselineMonth, asOfDate, showSnackbar]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const months = useMemo(() => data?.months || [], [data]);
    const currentMonth = months[months.length - 1] || null;
    const baselineMeta = months[0] || null;
    const hasComparison = metric === METRIC_DISPATCHED && months.length > 1;
    const totalsCurrent = data?.totals?.current || null;
    const getProjected = (current) => (projectOvertime ? current.projectedMonthEndWithOvertime : current.projectedMonthEnd);
    const projectionModeLabel = projectOvertime ? 'incl. expected weekend OT' : 'working days only';

    const asOfLabel = useMemo(() => {
        if (!data) return '';
        const asOf = moment.parseZone(data.asOf);
        return data.isLive
            ? `Live as at ${asOf.format('ddd D MMM YYYY HH:mm')}`
            : `As at close of ${asOf.format('ddd D MMM YYYY')}`;
    }, [data]);

    const comparisonLabel = useMemo(() => {
        if (!currentMonth) return '';
        const workingDays = currentMonth.completedWorkingDays;
        const endLabel = formatDay(currentMonth.comparisonEndDate);
        return `${workingDays} completed working day${workingDays === 1 ? '' : 's'} (to ${endLabel})`;
    }, [currentMonth]);

    const handleExportCsv = () => {
        if (!data) return;
        const header = ['Client', ...months.map((month) => `${month.label} ${getMonthSubtitle(month)}`)];
        if (hasComparison) {
            header.push(
                `${currentMonth.label} to ${formatDay(currentMonth.comparisonEndDate)}`,
                `${baselineMeta.label} at same point`,
                `vs ${baselineMeta.label}`,
                `Projected ${currentMonth.label} month end (${projectionModeLabel})`
            );
        }
        const toRow = (label, row) => {
            const cells = [label, ...months.map((month) => row.months[month.key]?.[metric] ?? 0)];
            if (hasComparison) {
                cells.push(
                    row.current.dispatchedToComparisonPoint,
                    row.current.baselineAtSamePoint,
                    formatPct(row.current.vsBaselineAtSamePointPct),
                    getProjected(row.current)
                );
            }
            return cells;
        };
        const lines = [
            [`Monthly Dispatch Tracker - ${metric === METRIC_DUE ? 'Orders due (cutoff-adjusted)' : 'Dispatched orders'}`],
            [asOfLabel],
            [],
            header,
            ...data.clients.map((row) => toRow(row.clientName, row)),
            toRow('Total', data.totals),
        ];
        if (metric === METRIC_DISPATCHED) {
            lines.push([
                'of which weekend / bank holiday overtime',
                ...months.map((month) => data.totals.months[month.key]?.dispatchedNonWorkingDays ?? 0),
            ]);
        }
        const csv = lines.map((cells) => cells.map(csvEscape).join(',')).join('\n');
        const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `monthly-dispatch-tracker-${data.asOfDate}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const headCellSx = { fontWeight: 700, whiteSpace: 'nowrap', backgroundColor: '#fafafa' };
    const numberCellSx = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
    const totalCellSx = { fontWeight: 700, borderTop: '2px solid', borderTopColor: 'text.primary' };
    const stickyClientSx = { position: 'sticky', left: 0, zIndex: 1, backgroundColor: '#fff', whiteSpace: 'nowrap' };

    const renderRowCells = (row, isTotal = false) => (
        <>
            {months.map((month) => (
                <TableCell
                    key={month.key}
                    align="right"
                    sx={{
                        ...numberCellSx,
                        ...(isTotal ? totalCellSx : {}),
                        ...(month.isCurrent ? { backgroundColor: 'rgba(231, 45, 136, 0.04)' } : {}),
                    }}
                >
                    {formatNumber(row.months[month.key]?.[metric] ?? 0)}
                    {metric === METRIC_DISPATCHED && row.months[month.key]?.dispatchedNonWorkingDays > 0 && (
                        <Typography
                            component="div"
                            variant="caption"
                            sx={{ color: OVERTIME_COLOR, fontWeight: isTotal ? 700 : 500, lineHeight: 1.2 }}
                        >
                            incl. {formatNumber(row.months[month.key].dispatchedNonWorkingDays)} wkend/BH
                        </Typography>
                    )}
                </TableCell>
            ))}
            {hasComparison && (
                <>
                    <TableCell align="right" sx={{ ...numberCellSx, ...(isTotal ? totalCellSx : {}) }}>
                        {formatNumber(row.current.dispatchedToComparisonPoint)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...numberCellSx, ...(isTotal ? totalCellSx : {}) }}>
                        {formatNumber(row.current.baselineAtSamePoint)}
                    </TableCell>
                    <TableCell
                        align="right"
                        sx={{
                            ...numberCellSx,
                            ...(isTotal ? totalCellSx : {}),
                            color: pctColor(row.current.vsBaselineAtSamePointPct),
                            fontWeight: 600,
                        }}
                    >
                        {formatPct(row.current.vsBaselineAtSamePointPct)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...numberCellSx, ...(isTotal ? totalCellSx : {}) }}>
                        {formatNumber(getProjected(row.current))}
                        {projectOvertime && row.current.projectedOvertime > 0 && (
                            <Typography
                                component="div"
                                variant="caption"
                                sx={{ color: OVERTIME_COLOR, fontWeight: isTotal ? 700 : 500, lineHeight: 1.2 }}
                            >
                                incl. {formatNumber(row.current.projectedOvertime)} OT
                            </Typography>
                        )}
                    </TableCell>
                </>
            )}
        </>
    );

    return (
        <Layout>
            <Box sx={{ p: { xs: 1, sm: 2 }, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                            Monthly Dispatch Tracker
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {data
                                ? `${asOfLabel} · Baseline ${months[0]?.label} · Showing ${months[0]?.label} to ${currentMonth?.label} (up to ${data.windowMonths} months)`
                                : 'Loading...'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                        <TextField
                            label="Baseline month"
                            type="month"
                            size="small"
                            value={baselineMonth}
                            onChange={(event) => {
                                const nextBaseline = event.target.value || DEFAULT_BASELINE_MONTH;
                                setBaselineMonth(nextBaseline);
                                if (asOfDate && asOfDate < `${nextBaseline}-01`) setAsOfDate('');
                            }}
                            slotProps={{
                                inputLabel: { shrink: true },
                                htmlInput: { max: moment().format('YYYY-MM') },
                            }}
                            sx={{ width: 170 }}
                        />
                        <TextField
                            label="As at (blank = live)"
                            type="date"
                            size="small"
                            value={asOfDate}
                            onChange={(event) => setAsOfDate(event.target.value)}
                            slotProps={{
                                inputLabel: { shrink: true },
                                htmlInput: { min: `${baselineMonth}-01`, max: moment().format('YYYY-MM-DD') },
                            }}
                            sx={{ width: 190 }}
                        />
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={metric}
                            onChange={(_, value) => value && setMetric(value)}
                        >
                            <ToggleButton value={METRIC_DISPATCHED}>Dispatched</ToggleButton>
                            <ToggleButton value={METRIC_DUE}>Orders due</ToggleButton>
                        </ToggleButtonGroup>
                        <MuiTooltip title={data?.definitions?.projectedWithOvertime || ''}>
                            <FormControlLabel
                                control={(
                                    <Switch
                                        size="small"
                                        checked={projectOvertime}
                                        onChange={(event) => setProjectOvertime(event.target.checked)}
                                    />
                                )}
                                label="Projection incl. weekend OT"
                                slotProps={{ typography: { variant: 'body2' } }}
                                sx={{ mx: 0.5 }}
                            />
                        </MuiTooltip>
                        <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadData} disabled={loading}>
                            Refresh
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<FileDownloadOutlinedIcon />}
                            onClick={handleExportCsv}
                            disabled={loading || !data}
                        >
                            Export CSV
                        </Button>
                    </Box>
                </Box>

                {loading && !data && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                )}

                {data && currentMonth && totalsCurrent && (
                    <>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                            <SummaryCard
                                title={`${currentMonth.label} dispatched ${currentMonth.isComplete ? '' : 'MTD'}`}
                                value={formatNumber(totalsCurrent.dispatched)}
                                detail={`Working day ${currentMonth.elapsedWorkingDays} of ${currentMonth.totalWorkingDays}`}
                            />
                            {months.length > 1 && (
                                <SummaryCard
                                    title={`vs ${baselineMeta.label} at same point`}
                                    value={formatPct(totalsCurrent.vsBaselineAtSamePointPct)}
                                    valueColor={pctColor(totalsCurrent.vsBaselineAtSamePointPct)}
                                    accent={pctColor(totalsCurrent.vsBaselineAtSamePointPct)}
                                    detail={`${formatNumber(totalsCurrent.dispatchedToComparisonPoint)} vs ${formatNumber(totalsCurrent.baselineAtSamePoint)} after ${comparisonLabel}`}
                                />
                            )}
                            {months.length > 2 && (
                                <SummaryCard
                                    title={`vs ${months[months.length - 2].label} at same point`}
                                    value={formatPct(totalsCurrent.vsPreviousMonthAtSamePointPct)}
                                    valueColor={pctColor(totalsCurrent.vsPreviousMonthAtSamePointPct)}
                                    accent={pctColor(totalsCurrent.vsPreviousMonthAtSamePointPct)}
                                    detail={`${formatNumber(totalsCurrent.dispatchedToComparisonPoint)} vs ${formatNumber(totalsCurrent.previousMonthAtSamePoint)}`}
                                />
                            )}
                            <SummaryCard
                                title={`Projected ${currentMonth.label} month end (${projectionModeLabel})`}
                                value={formatNumber(getProjected(totalsCurrent))}
                                accent={projectOvertime ? OVERTIME_COLOR : 'info.main'}
                                detail={[
                                    months.length > 1
                                        ? `${formatPct(projectOvertime
                                            ? totalsCurrent.projectedWithOvertimeVsBaselineFullMonthPct
                                            : totalsCurrent.projectedVsBaselineFullMonthPct)} vs ${baselineMeta.label} full month (${formatNumber(data.totals.months[baselineMeta.key].dispatched)})`
                                        : 'Based on average per working day',
                                    projectOvertime && !currentMonth.isComplete
                                        ? `+${formatNumber(totalsCurrent.projectedOvertime)} expected OT over ${totalsCurrent.remainingNonWorkingDays} weekend/BH day${totalsCurrent.remainingNonWorkingDays === 1 ? '' : 's'} left (${formatNumber(totalsCurrent.overtimeAvgPerNonWorkingDay)}/day; OT worked on ${totalsCurrent.overtimeHistoryDaysWorked} of ${totalsCurrent.overtimeHistoryNonWorkingDays} weekend/BH days since ${baselineMeta.label})`
                                        : null,
                                ].filter(Boolean).join(' · ')}
                            />
                            <SummaryCard
                                title="Avg per working day"
                                value={formatNumber(data.totals.months[currentMonth.key].avgPerWorkingDay)}
                                accent="secondary.main"
                                detail={months.length > 1
                                    ? `Mon–Fri only, excl. overtime · ${baselineMeta.label}: ${formatNumber(data.totals.months[baselineMeta.key].avgPerWorkingDay)}`
                                    : 'Mon–Fri only, excl. overtime'}
                            />
                            <SummaryCard
                                title="Weekend / BH overtime"
                                value={formatNumber(data.totals.months[currentMonth.key].dispatchedNonWorkingDays)}
                                accent={OVERTIME_COLOR}
                                valueColor={data.totals.months[currentMonth.key].dispatchedNonWorkingDays > 0 ? OVERTIME_COLOR : 'text.primary'}
                                detail={months.length > 1
                                    ? `Dispatched Sat/Sun/BH in ${currentMonth.label} · ${baselineMeta.label}: ${formatNumber(data.totals.months[baselineMeta.key].dispatchedNonWorkingDays)}`
                                    : `Dispatched Sat/Sun/BH in ${currentMonth.label}`}
                            />
                        </Box>

                        <Paper elevation={2} sx={{ mb: 2 }}>
                            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                    {metric === METRIC_DUE ? 'Orders due by client (cutoff-adjusted)' : 'No. dispatched by client'}
                                </Typography>
                                {hasComparison && (
                                    <Typography variant="caption" color="text.secondary">
                                        Totals include every calendar day (weekend/bank holiday overtime shown in orange). Same-point
                                        comparison uses {comparisonLabel} against the same number of working days in {baselineMeta.label}.
                                    </Typography>
                                )}
                            </Box>
                            <TableContainer sx={{ maxHeight: '70vh' }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ ...headCellSx, ...stickyClientSx, backgroundColor: '#fafafa', zIndex: 3 }}>
                                                Client
                                            </TableCell>
                                            {months.map((month) => (
                                                <TableCell key={month.key} align="right" sx={headCellSx}>
                                                    <Box>{month.label}</Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {getMonthSubtitle(month)}
                                                    </Typography>
                                                </TableCell>
                                            ))}
                                            {hasComparison && (
                                                <>
                                                    <TableCell align="right" sx={headCellSx}>
                                                        <Box>{currentMonth.label}</Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            (to {formatDay(currentMonth.comparisonEndDate)})
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right" sx={headCellSx}>
                                                        <Box>{baselineMeta.label}</Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            (same point)
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right" sx={headCellSx}>
                                                        vs {baselineMeta.label}
                                                    </TableCell>
                                                    <MuiTooltip title={projectOvertime ? data.definitions.projectedWithOvertime : data.definitions.projectedMonthEnd}>
                                                        <TableCell align="right" sx={headCellSx}>
                                                            <Box>Projected</Box>
                                                            <Typography variant="caption" color="text.secondary" component="div">
                                                                ({currentMonth.label} month end)
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                component="div"
                                                                sx={{ color: projectOvertime ? OVERTIME_COLOR : 'text.secondary' }}
                                                            >
                                                                {projectionModeLabel}
                                                            </Typography>
                                                        </TableCell>
                                                    </MuiTooltip>
                                                </>
                                            )}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.clients.map((row) => (
                                            <TableRow key={row.clientName} hover>
                                                <TableCell sx={stickyClientSx}>{row.clientName}</TableCell>
                                                {renderRowCells(row)}
                                            </TableRow>
                                        ))}
                                        <TableRow>
                                            <TableCell sx={{ ...stickyClientSx, ...totalCellSx }}>Total</TableCell>
                                            {renderRowCells(data.totals, true)}
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>

                        <DailyDispatchChart key={`daily-${currentMonth.key}`} months={months} totals={data.totals} />

                        <CumulativePaceChart months={months} totals={data.totals} isLive={data.isLive} />

                        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                                How the numbers are counted
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                                    gap: 1.5,
                                }}
                            >
                                {[
                                    ['Dispatched', data.definitions.dispatched],
                                    ['Orders due', data.definitions.due],
                                    ['Same point', data.definitions.samePoint],
                                    ['Projected month end', data.definitions.projectedMonthEnd],
                                    ['Projected incl. weekend OT', data.definitions.projectedWithOvertime],
                                ].map(([label, text]) => (
                                    <Box
                                        key={label}
                                        sx={{ p: 1.5, borderRadius: 1, backgroundColor: '#fafafa', border: '1px solid', borderColor: 'divider' }}
                                    >
                                        <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                            {label}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                                            {text}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                    </>
                )}
            </Box>
        </Layout>
    );
};

export default MonthlyDispatchTracker;
